import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { postImageUrl } from '../../actions/users';
import { sendToastMessage } from '../../actions/toast';
import { extractPavAsset } from './pavConfig';
import { PAV_PENDING_ATTACHMENT_STATUSES, createAttachmentId } from './pavAssetUtils';

// Every PAV image goes through OCR, including the ones added from inside an
// asset card. The card the image was added to keeps it, so the reading fills
// that card instead of producing another one.
export default function usePavAttachments({ ticketId, milestoneId, scopeId, initialAttachments = [] }) {
    const dispatch = useDispatch();
    const [attachments, setAttachments] = useState(() => initialAttachments);
    const attachmentRef = useRef([]);

    useEffect(() => {
        attachmentRef.current = attachments;
    }, [attachments]);

    useEffect(() => () => {
        attachmentRef.current.forEach((attachment) => {
            if (attachment.preview_url) URL.revokeObjectURL(attachment.preview_url);
        });
    }, []);

    const updateAttachment = (id, updates) => {
        setAttachments((current) => current.map((attachment) => (
            attachment.id === id ? { ...attachment, ...updates } : attachment
        )));
    };

    const extractAttachment = async (attachment, uploadedFile) => {
        const fileUrl = typeof uploadedFile === 'string' ? uploadedFile : uploadedFile?.file_url;
        if (!fileUrl) {
            updateAttachment(attachment.id, { status: 'error', error: 'Upload completed without a file URL.' });
            return;
        }

        updateAttachment(attachment.id, { status: 'extracting', url: fileUrl });
        try {
            const ocrData = await extractPavAsset(fileUrl);
            updateAttachment(attachment.id, { status: 'complete', ocr_data: ocrData, error: '' });
        } catch (error) {
            updateAttachment(attachment.id, { status: 'error', error: error.message || 'OCR failed for this image.' });
        }
    };

    // Returns the ids of the attachments it queued so callers can pin a fresh
    // upload to a specific asset card.
    const addFiles = (files) => {
        const selectedFiles = Array.from(files || []);
        if (selectedFiles.length === 0) return [];

        const invalidFiles = selectedFiles.filter((file) => !file.type?.startsWith('image/'));
        if (invalidFiles.length > 0) {
            dispatch(sendToastMessage({ status: 'danger', message: 'Only image attachments are supported for PAV.' }));
        }

        const existingFiles = new Set(attachmentRef.current.map((attachment) => `${attachment.name}:${attachment.size}`));
        const imageFiles = selectedFiles.filter((file) => {
            const fileKey = `${file.name}:${file.size}`;
            if (!file.type?.startsWith('image/') || existingFiles.has(fileKey)) return false;
            existingFiles.add(fileKey);
            return true;
        });
        if (imageFiles.length === 0) return [];

        const queuedAttachments = imageFiles.map((file, index) => ({
            id: createAttachmentId(file, index),
            file,
            name: file.name,
            mime_type: file.type,
            size: file.size,
            preview_url: URL.createObjectURL(file),
            url: null,
            status: index === 0 ? 'uploading' : 'queued',
            error: '',
            ocr_data: null,
        }));
        setAttachments((current) => [...current, ...queuedAttachments]);

        // Keep signed uploads sequential, then hand each one to OCR as it lands.
        const uploadNextAttachment = (index) => {
            if (index >= queuedAttachments.length) return;
            const attachment = queuedAttachments[index];
            const file = imageFiles[index];
            updateAttachment(attachment.id, { status: 'uploading', error: '' });

            dispatch(postImageUrl({
                files: [file],
                ref: `pav-${ticketId}-${milestoneId}-${scopeId}-${index}-${Date.now()}`,
                purpose: 'PAV_ATTACHMENT',
                ticket_id: ticketId,
                onSuccess: (uploadedFiles) => {
                    extractAttachment(attachment, Array.isArray(uploadedFiles) ? uploadedFiles[0] : uploadedFiles);
                    setTimeout(() => uploadNextAttachment(index + 1), 0);
                },
                onError: (error) => {
                    updateAttachment(attachment.id, { status: 'error', error: error?.message || 'Image upload failed.' });
                    setTimeout(() => uploadNextAttachment(index + 1), 0);
                },
            }));
        };

        uploadNextAttachment(0);
        return queuedAttachments.map((attachment) => attachment.id);
    };

    const removeAttachments = (attachmentIds) => {
        const idsToRemove = new Set(attachmentIds);
        setAttachments((current) => {
            current.forEach((attachment) => {
                if (idsToRemove.has(attachment.id) && attachment.preview_url) URL.revokeObjectURL(attachment.preview_url);
            });
            return current.filter((attachment) => !idsToRemove.has(attachment.id));
        });
    };

    const getTransferAttachments = () => attachmentRef.current.map((attachment) => ({
        ...attachment,
        preview_url: attachment.file ? URL.createObjectURL(attachment.file) : attachment.preview_url,
    }));

    const isProcessing = attachments.some((attachment) => PAV_PENDING_ATTACHMENT_STATUSES.includes(attachment.status));
    const processedCount = attachments.filter((attachment) => ['complete', 'error'].includes(attachment.status)).length;
    const hasFailedAttachment = attachments.some((attachment) => attachment.status === 'error');

    return {
        attachments,
        addFiles,
        removeAttachment: (attachmentId) => removeAttachments([attachmentId]),
        removeAttachments,
        getTransferAttachments,
        isProcessing,
        processedCount,
        hasFailedAttachment,
    };
}
